import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Popup from "../../../components/Popup";
import AxionControls from "../../../components/controls/AxionControls";
import { replaceTradeMembers } from "../../../fetchers/cmmsTrade";
import { fetchUsers } from "../../../fetchers/user";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { formatCmmsError } from "../../cmms/components/CmmsStates";
import {
  getAssetHierarchyPalette,
  getContainedButtonSx,
  getOutlinedButtonSx,
} from "../../cmms/assetHierarchyStyles";
import cmmsMessages from "../../cmms/cmmsMessages";
import FieldError from "../../cmmsSpareParts/components/FieldError";
import messages from "../messages";
import { toTradeMemberOptions } from "../tradeMemberOptions";

/*
 * Who is in a trade.
 *
 * One picker and a Save, posting the whole list: the endpoint is a replace-set, so what is in the
 * field when Save is pressed is the membership afterwards. Adding and removing one at a time
 * would be a call per chip and a half-saved trade whenever one of them failed.
 *
 * Its own dialog rather than a field on TradeFormDialog, because they are separate calls on the
 * server and a rename that failed should not take a membership change down with it.
 *
 * The users are the ["users"] query the work order forms already share, so opening this after one
 * of those costs nothing.
 */
export default function TradeMembersDialog({ open, onClose, trade, companyKey }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();
  const queryClient = useQueryClient();

  const [selectedIds, setSelectedIds] = useState([]);

  const usersQuery = useQuery(["users"], () => fetchUsers(), { enabled: open, staleTime: 300000 });

  // Seeded each time it opens, from the row it was opened on. Not kept in step with later
  // refetches of the list: an edit in progress is not something a background refresh should undo.
  useEffect(() => {
    if (!open) return;
    setSelectedIds((trade?.members ?? []).map((member) => member.userId));
  }, [open, trade]);

  const options = useMemo(
    () => toTradeMemberOptions(usersQuery.data, selectedIds, trade?.members),
    [usersQuery.data, selectedIds, trade],
  );

  // In the order they were picked: the saved members first, in the server's name order, and
  // anyone added since after them, so a new chip lands where the eye expects it.
  const selectedOptions = useMemo(
    () => selectedIds
      .map((id) => options.find((option) => option.id === id))
      .filter(Boolean),
    [selectedIds, options],
  );

  // The picker draws every chip alike, so the members it is keeping despite the rule are named
  // underneath instead.
  const staleNames = selectedOptions
    .filter((option) => !option.isTechnician)
    .map((option) => option.title);

  const saveMutation = useMutation({
    mutationFn: (userIds) => replaceTradeMembers(companyKey, trade.id, userIds),
  });

  const handleSubmit = async () => {
    try {
      await saveMutation.mutateAsync(selectedIds);
      await queryClient.invalidateQueries({ queryKey: ["cmmsTrades"] });
      pushNotification(intl.formatMessage(messages.tradeToastMembersUpdated));
      onClose?.();
    } catch (error) {
      // The server's own words, which name the user when someone is not a technician.
      pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.tradeMembersSaveFailed)));
    }
  };

  const submitting = saveMutation.isLoading;

  return (
    <Popup
      openPopup={open}
      setOpenPopup={(value) => { if (!value) onClose?.(); }}
      maxWidth="sm"
      title={intl.formatMessage(messages.tradeMembersTitle, { name: trade?.name ?? "" })}
    >
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        <Box>
          <AxionControls.AxionAutocomplete
            name="tradeMembers"
            label={intl.formatMessage(messages.tradeMembersFieldLabel)}
            multiple
            options={options}
            value={selectedOptions}
            onChange={(_event, picked) => setSelectedIds((picked ?? []).map((option) => option.id))}
            // Until the users arrive the only options are the members already in it, and a picker
            // that offers nobody to add looks like a trade nobody can join.
            disabled={usersQuery.isLoading || submitting}
          />
          {/*
            * Not a dead end. The saved members are in the options regardless, so the dialog can
            * still take people out; it only cannot offer anyone new.
            */}
          {usersQuery.isError &&
            <FieldError message={intl.formatMessage(messages.tradeMembersUsersLoadFailed)} />
          }
          {staleNames.length > 0 &&
            <Typography variant="caption" component="p" sx={{ color: "warning.main", mt: 0.5 }}>
              <FormattedMessage {...messages.tradeMembersStaleHint} values={{ names: staleNames.join(", ") }} />
            </Typography>
          }
          <Typography variant="caption" component="p" sx={{ color: "text.secondary", mt: 0.5 }}>
            <FormattedMessage {...messages.tradeMembersFieldHint} />
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={submitting} sx={getOutlinedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.cancel} />
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !trade?.id}
            sx={getContainedButtonSx(cmmsColors)}
          >
            <FormattedMessage {...cmmsMessages.save} />
          </Button>
        </Stack>
      </Box>
      <AxionControls.AxionBackdrop open={submitting} />
    </Popup>
  );
}
